import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UpdateCmmdSavedDataViewParams } from "@cmmd-forge/contracts";
import {
  dataViewStatesEqual,
  EMPTY_DATA_VIEW_STATE,
  persistedDataViewStatesEqual,
  toCmmdSavedDataViewDefinition,
  type DataViewState,
  type DataViewSurface,
  type SavedDataView,
  type SavedDataViewDraft,
} from "./viewDefinition";
import {
  loadSavedViews,
  mutateSavedView,
  isSavedViewConflict,
  savedViewErrorMessage,
  savedViewScope,
  savedViewsQueryKey,
  upsertSavedView,
} from "./viewControllerPersistence";

export interface DataViewRouteState {
  readonly savedViewId: string;
  readonly state: DataViewState;
  /** Distinguishes an explicitly selected baseline from an unseen route. */
  readonly hasExplicitState?: boolean;
}

export interface DataViewController {
  readonly surface: DataViewSurface;
  readonly state: DataViewState;
  readonly savedViews: readonly SavedDataView[];
  readonly activeSavedView: SavedDataView | null;
  readonly isDirty: boolean;
  readonly isLoading: boolean;
  readonly isMutating: boolean;
  readonly error: string | null;
  readonly setState: (state: DataViewState, savedViewId?: string | null) => void;
  readonly patchState: (patch: Partial<DataViewState>) => void;
  readonly resetState: () => void;
  readonly applySavedView: (viewId: string) => void;
  readonly saveView: (draft: SavedDataViewDraft) => Promise<SavedDataView | null>;
  readonly updateActiveView: () => Promise<SavedDataView | null>;
  readonly renameView: (viewId: string, name: string) => Promise<void>;
  readonly deleteView: (viewId: string) => Promise<void>;
  readonly setDefaultView: (viewId: string | null) => Promise<void>;
  readonly refresh: () => Promise<void>;
}

export interface UseDataViewControllerParams {
  readonly surface: DataViewSurface;
  readonly projectId?: number;
  readonly enabled?: boolean;
  readonly route: DataViewRouteState;
  readonly fallbackState?: DataViewState;
  readonly onRouteChange: (
    state: DataViewState,
    savedViewId: string | null,
    previousState: DataViewState,
    previousSavedViewId: string | null,
  ) => void;
}

/** Explicit baseline selection, distinct from an absent selection that applies a canonical default. */
export const DEFAULT_VIEW_SELECTION = "__default__";

function defaultSavedView(savedViews: readonly SavedDataView[]): SavedDataView | null {
  return savedViews.find((view) => view.isDefault) ?? null;
}

function findSavedView(savedViews: readonly SavedDataView[], viewId: string): SavedDataView | null {
  return viewId.length > 0 ? (savedViews.find((view) => view.id === viewId) ?? null) : null;
}

function hasRouteState(route: DataViewRouteState): boolean {
  return (
    route.hasExplicitState === true || !dataViewStatesEqual(route.state, EMPTY_DATA_VIEW_STATE)
  );
}

/** Keep URL/session task filters when CMMD round-trips a layout+search definition. */
function retainEphemeralTaskFilters(
  surface: DataViewSurface,
  previous: DataViewState,
  next: DataViewState,
): DataViewState {
  if (surface !== "cmmd.project.tasks") return next;
  return {
    ...next,
    statusFilter: previous.statusFilter,
    ownerFilter: previous.ownerFilter,
    milestoneFilter: previous.milestoneFilter,
    taskGroupBy: previous.taskGroupBy,
    taskSort: previous.taskSort ?? null,
  };
}

export function resolveDataViewRouteState(
  savedViews: readonly SavedDataView[],
  route: DataViewRouteState,
  fallbackState: DataViewState,
): { readonly state: DataViewState; readonly savedViewId: string | null } {
  if (route.savedViewId === DEFAULT_VIEW_SELECTION) {
    return {
      state: hasRouteState(route) ? route.state : fallbackState,
      savedViewId: DEFAULT_VIEW_SELECTION,
    };
  }
  const savedView = findSavedView(savedViews, route.savedViewId);
  if (savedView) {
    return {
      state: hasRouteState(route) ? route.state : savedView.state,
      savedViewId: savedView.id,
    };
  }
  if (hasRouteState(route)) {
    return { state: route.state, savedViewId: null };
  }
  const defaultView = defaultSavedView(savedViews);
  return {
    state: defaultView?.state ?? fallbackState,
    savedViewId: defaultView?.id ?? null,
  };
}

export function useDataViewController({
  surface,
  projectId,
  enabled = true,
  route,
  fallbackState = EMPTY_DATA_VIEW_STATE,
  onRouteChange,
}: UseDataViewControllerParams): DataViewController {
  const queryClient = useQueryClient();
  const queryKey = savedViewsQueryKey(surface, projectId);
  const query = useQuery({
    queryKey,
    queryFn: () => loadSavedViews(surface, projectId),
    staleTime: 30_000,
    enabled,
  });
  const mutation = useMutation({
    mutationFn: mutateSavedView,
    onSuccess: (result) => {
      queryClient.setQueryData<readonly SavedDataView[]>(queryKey, (current) =>
        result.kind === "upsert"
          ? upsertSavedView(current, result.view)
          : (current ?? []).filter((view) => view.id !== result.viewId),
      );
    },
    onError: (error) => {
      if (isSavedViewConflict(error)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  const savedViews = query.data ?? [];
  const resolved = resolveDataViewRouteState(savedViews, route, fallbackState);
  const activeSavedView =
    resolved.savedViewId && resolved.savedViewId !== DEFAULT_VIEW_SELECTION
      ? findSavedView(savedViews, resolved.savedViewId)
      : null;
  const state = resolved.state;
  const isDirty = activeSavedView
    ? !persistedDataViewStatesEqual(surface, activeSavedView.state, state)
    : false;
  const scopeFields = savedViewScope(surface, projectId);

  const setState = (
    nextState: DataViewState,
    savedViewId: string | null = resolved.savedViewId,
  ) => {
    onRouteChange(nextState, savedViewId, state, resolved.savedViewId);
  };

  const updateView = async (
    view: SavedDataView,
    patch: Omit<UpdateCmmdSavedDataViewParams, "surface" | "projectId" | "expectedRevision">,
  ): Promise<SavedDataView> => {
    const result = await mutation.mutateAsync({
      kind: "update",
      viewId: view.id,
      params: { ...scopeFields, expectedRevision: view.revision, ...patch },
    });
    if (result.kind !== "upsert") throw new Error("Saved view update returned no view.");
    return result.view;
  };

  return {
    surface,
    state,
    savedViews,
    activeSavedView,
    isDirty,
    isLoading: query.isLoading,
    isMutating: mutation.isPending,
    error: query.error
      ? savedViewErrorMessage(query.error)
      : mutation.error
        ? savedViewErrorMessage(mutation.error)
        : null,
    setState,
    patchState: (patch) => setState({ ...state, ...patch }),
    resetState: () => setState({ ...fallbackState, layout: state.layout }, DEFAULT_VIEW_SELECTION),
    applySavedView: (viewId) => {
      const view = findSavedView(savedViews, viewId);
      // Task status/assignee filters are URL-local; Reset must not wipe them.
      if (view) setState(retainEphemeralTaskFilters(surface, state, view.state), view.id);
    },
    saveView: async (draft) => {
      const name = draft.name.trim();
      if (name.length === 0) return null;
      const result = await mutation.mutateAsync({
        kind: "create",
        params: {
          ...scopeFields,
          name,
          definition: toCmmdSavedDataViewDefinition(surface, draft.state),
          isDefault: draft.setDefault,
        },
      });
      if (result.kind !== "upsert") return null;
      setState(retainEphemeralTaskFilters(surface, state, result.view.state), result.view.id);
      return result.view;
    },
    updateActiveView: async () => {
      if (!activeSavedView) return null;
      const updated = await updateView(activeSavedView, {
        definition: toCmmdSavedDataViewDefinition(surface, state),
      });
      setState(retainEphemeralTaskFilters(surface, state, updated.state), updated.id);
      return updated;
    },
    renameView: async (viewId, name) => {
      const view = findSavedView(savedViews, viewId);
      const trimmed = name.trim();
      if (!view || trimmed.length === 0 || trimmed === view.name) return;
      await updateView(view, { name: trimmed });
    },
    deleteView: async (viewId) => {
      const view = findSavedView(savedViews, viewId);
      if (!view) return;
      await mutation.mutateAsync({
        kind: "delete",
        viewId,
        params: { ...scopeFields, expectedRevision: view.revision },
      });
      if (resolved.savedViewId === viewId) {
        const remaining = savedViews.filter((candidate) => candidate.id !== viewId);
        const next = defaultSavedView(remaining);
        setState(next?.state ?? fallbackState, next?.id ?? null);
      }
    },
    setDefaultView: async (viewId) => {
      const target = viewId ? findSavedView(savedViews, viewId) : defaultSavedView(savedViews);
      if (!target || target.isDefault === (viewId !== null)) return;
      await updateView(target, { isDefault: viewId !== null });
    },
    refresh: async () => {
      await query.refetch();
    },
  };
}
